import { useTFFLocations } from "@features/tff/useTFFLocations"
import { RouteState, useHydrating, useRoute, useRouter, useRouterEvents } from "eddev/routing"
import { useEffect, useMemo, useState } from "react"
import { proxy, subscribe, useSnapshot } from "valtio"

type ParsedTFFRoute = {
  isTFF: boolean
  centerSlug: string | null
}

function parseTFFRoute(route: RouteState): ParsedTFFRoute {
  let isTFF = false
  let centerSlug: string | null = null
  if (route.pathname.startsWith("/tff")) {
    isTFF = true
  }
  if (isTFF) {
    if (route.view === "single-tff-center") {
      centerSlug = route.props.tffCenter?.slug!
    } else if (route.view === "single-tff-center-tickets") {
      centerSlug = route.props.tffCenter?.slug!
    } else if (route.view === "single-film") {
      centerSlug = route.props.film?.tffCenter?.slug!
    }
  }

  return {
    isTFF,
    centerSlug,
  }
}

const store = proxy({
  centerSlug: null as string | null,
  setPreferredCenter: (slug: string | null) => {
    store.centerSlug = slug
  },
})

if (env.client) {
  store.setPreferredCenter(sessionStorage.getItem("lastTffCenter") || null)
  subscribe(store, () => {
    sessionStorage.setItem("lastTffCenter", store.centerSlug || "")
  })
}

export function useTFF() {
  const hydrating = useHydrating()
  const route = useRoute()
  const parsed = useMemo(() => parseTFFRoute(route), [route])
  const state = useSnapshot(store)
  const locations = useTFFLocations()

  useEffect(() => {
    if (parsed.isTFF && parsed.centerSlug) {
      store.setPreferredCenter(parsed.centerSlug)
    }
  }, [parsed.isTFF, parsed.centerSlug])

  const centerSlug = parsed.centerSlug ?? (hydrating ? null : state.centerSlug)

  return {
    isTFF: parsed.isTFF,
    centerSlug: centerSlug,
    /** The TFF center, as included in the current route. */
    routeCenter: locations?.find((l) => l.slug === parsed.centerSlug) || null,
    /** The TFF center, which is either the center in the route, OR the fallback location based on user preference */
    center: locations?.find((l) => l.slug === centerSlug) || null,
  }
}
